import { useMemo } from "react"
import { useQuery } from "@tanstack/react-query"
import { constants } from "helpers"
import useChainProviderHook from "@/hooks/useChainProvider"
import {
  transformSxActiveMarkets,
  transformSxMarketsForComponents,
} from "../utils/transformSxMarkets"
import type {
  SxSummaryFixture,
  SxSummarySport,
  SxSummaryResponse,
} from "../types/markets"
import { SX_CONFIG } from "../constants"

// Result interface for the hook
interface UseSxHomeMarketsResult {
  sports: SxSummarySport[]
  fixtures: SxSummaryFixture[]
  markets: any[]
  marketsCount: number
  isLoading: boolean
  isFetching: boolean // For live switch transitions
  error: Error | null
}

/**
 * Helper function to fetch upcoming markets summary
 */
const fetchUpcomingSummary = async (
  chainId: number
): Promise<SxSummaryResponse> => {
  const TOKEN = SX_CONFIG[chainId].CONTRACTS.TOKEN
  const url = `${constants.getSxBetApiUrl()}/summary/upcoming/${TOKEN}?chainId=${chainId}`

  const response = await fetch(url)
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`)
  }

  const result: SxSummaryResponse = await response.json()
  if (result.status !== "success") {
    throw new Error(`API error: ${result.status}`)
  }

  return result
}

/**
 * Main hook for fetching SX home markets from the summary/upcoming API
 */
export const useSxHomeMarkets = (
  enabled: boolean = true
): UseSxHomeMarketsResult => {
  const { chainId, isSXChain } = useChainProviderHook()
  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ["sx-home-markets", chainId],
    queryFn: async () => {
      return await fetchUpcomingSummary(chainId)
    },
    enabled: enabled && !!chainId,
    gcTime: 1000 * 30, // 30 seconds
    staleTime: 1000 * 30, // 30 seconds
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    refetchOnReconnect: true,
  })

  // Process the data using the new transform function
  const processedData = useMemo(() => {
    if (!data?.data?.sports || data.data.sports.length === 0) {
      return {
        sports: [],
        fixtures: [],
        markets: [],
        marketsCount: 0,
      }
    }

    const transformedSports = transformSxActiveMarkets(
      data.data.sports,
      chainId
    )

    // Transform markets for SXOutcomeButton compatibility
    const transformedMarkets = transformSxMarketsForComponents(
      data.data.sports,
      chainId,
      isSXChain
    )

    const fixtures: SxSummaryFixture[] = []
    const validFixtures: SxSummaryFixture[] = []
    let marketsCount = 0

    // Collect fixtures and count only fixtures with valid markets
    data.data.sports.forEach(sport => {
      sport.leagues.forEach(league => {
        league.fixtures.forEach(fixture => {
          fixtures.push(fixture)
          // Only count fixtures that have at least one market with valid odds
          if (
            fixture.markets &&
            fixture.markets.some(
              market => market.bestOddsOutcomeOne && market.bestOddsOutcomeTwo
            )
          ) {
            validFixtures.push(fixture)
          }
        })
      })
    })

    // Total number of valid fixtures (games with betting markets)
    marketsCount = validFixtures.length

    return {
      sports: transformedSports,
      fixtures,
      markets: transformedMarkets,
      marketsCount, // Using total fixtures count instead of markets count
    }
  }, [data, chainId])

  return {
    sports: processedData.sports,
    fixtures: processedData.fixtures,
    markets: processedData.markets,
    marketsCount: processedData.marketsCount,
    isLoading: isLoading,
    isFetching,
    error: error as Error | null,
  }
}

/**
 * Hook to get fixtures by sport ID
 */
export const useSxFixturesBySport = (
  sportId: number,
  enabled: boolean = true
): SxSummaryFixture[] => {
  const { sports } = useSxHomeMarkets(enabled)

  return useMemo(() => {
    const sport = sports.find(s => s.sportId === sportId)
    if (!sport) {
      return []
    }

    return sport.leagues.flatMap(league => league.fixtures)
  }, [sports, sportId])
}

/**
 * Hook to get fixtures by league ID
 */
export const useSxFixturesByLeague = (
  leagueId: number,
  enabled: boolean = true
): SxSummaryFixture[] => {
  const { fixtures } = useSxHomeMarkets(enabled)

  return useMemo(() => {
    return fixtures.filter(fixture => fixture.leagueId === leagueId)
  }, [fixtures, leagueId])
}

/**
 * Hook to get a specific fixture by ID
 */
export const useSxFixtureById = (
  fixtureId: string,
  enabled: boolean = true
): SxSummaryFixture | null => {
  const { fixtures } = useSxHomeMarkets(enabled)

  return useMemo(() => {
    return fixtures.find(fixture => fixture.id === fixtureId) || null
  }, [fixtures, fixtureId])
}

/**
 * Legacy hook name for backward compatibility
 * @deprecated Use useSxHomeMarkets instead
 */
export const useSxStreamingActiveMarkets = useSxHomeMarkets
